/**
 * Changer la langue affichée **sans toucher à la préférence du visiteur** —
 * chunk 9.BJ.6.
 *
 * ## Le problème que ça résout
 *
 * `i18n.changeLanguage()` déclenche `cacheUserLanguage` du détecteur, qui écrit
 * la langue dans `localStorage` sous `kollectivia-locale`. C'est le bon
 * comportement quand une personne CHOISIT sa langue — le sélecteur de la coque
 * publique, par exemple.
 *
 * Ce n'en est pas un quand la page **impose** une langue : un mandat rédigé en
 * français s'affiche en français parce que le document est français, pas parce
 * que le visiteur a exprimé une préférence. Laisser cette contrainte s'inscrire
 * durablement ferait basculer l'interface de quelqu'un qui n'a rien demandé —
 * et le courtier qui ouvre un lien de test dans son propre navigateur verrait sa
 * langue d'application changer.
 *
 * ## Comment
 *
 * On relit la valeur mise en cache après le changement et on restaure celle
 * d'avant — ou on retire la clé si elle n'existait pas. `changeLanguage` est
 * attendue avant la restauration : le détecteur écrit pendant l'événement
 * `languageChanged`, donc avant que la promesse ne se résolve.
 *
 * Le rendu courant, lui, reste bien dans la langue demandée : seule la trace
 * persistante est annulée.
 */

import type { i18n as I18nType } from "i18next";

/** La clé du détecteur — doit rester alignée sur `detection.lookupLocalStorage`. */
export const CLE_LOCALE = "kollectivia-locale";

function lire(): string | null {
  try {
    return localStorage.getItem(CLE_LOCALE);
  } catch {
    // Navigation privée, stockage bloqué : il n'y a alors rien à préserver.
    return null;
  }
}

function restaurer(precedent: string | null): void {
  try {
    if (precedent === null) localStorage.removeItem(CLE_LOCALE);
    else localStorage.setItem(CLE_LOCALE, precedent);
  } catch {
    /* rien à faire : sans stockage, rien n'a été écrasé non plus */
  }
}

/**
 * Applique une langue au rendu courant, sans la mémoriser.
 *
 * Ne fait rien si la langue est déjà celle affichée — un `changeLanguage`
 * inutile déclenche un re-rendu de tout l'arbre.
 */
export async function appliquerLangueEphemere(i18n: I18nType, langue: "fr" | "en"): Promise<void> {
  if (i18n.resolvedLanguage === langue) return;
  const precedent = lire();
  await i18n.changeLanguage(langue);
  restaurer(precedent);
}
